import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "../i18n";
import { LanguageSelector } from "../components/LanguageSelector";
import { 
  Globe, 
  Users, 
  Heart, 
  Trophy, 
  ArrowRight, 
  Instagram, 
  Youtube, 
  Music, 
  Coffee,
  ExternalLink,
  ChevronRight,
  MapPin,
  ShoppingBag,
  CreditCard,
  MessageCircle,
  Network,
  Shield,
  Ticket,
  Clock,
  FileText,
  CheckCircle2
} from "lucide-react";
import { Footer } from "../components/Footer";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";
// English football stadium - Premier League atmosphere
const HERO_BG = "https://images.unsplash.com/photo-1577214250144-73af4b7364f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxmb290YmFsbCUyMG1hdGNoJTIwc3RhZGl1bSUyMG5pZ2h0JTIwbGlnaHRzJTIwRW5nbGFuZHxlbnwwfHx8fDE3NzU4MTc3ODZ8MA&ixlib=rb-4.1.0&q=85";
// English football stadium image for Why section
const WHY_IMAGE = "https://images.unsplash.com/photo-1591337621106-50811bdf60d5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxQcmVtaWVyJTIwTGVhZ3VlJTIwZm9vdGJhbGwlMjBzdGFkaXVtJTIwY3Jvd2QlMjBFbmdsYW5kfGVufDB8fHx8MTc3NTgxNzc3N3ww&ixlib=rb-4.1.0&q=85";
// Final CTA background - stadium match
const CTA_BG = "https://images.unsplash.com/photo-1626078859283-7e83d7e4137f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxmb290YmFsbCUyMG1hdGNoJTIwc3RhZGl1bSUyMG5pZ2h0JTIwbGlnaHRzJTIwRW5nbGFuZHxlbnwwfHx8fDE3NzU4MTc3ODZ8MA&ixlib=rb-4.1.0&q=85";

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/afrofanthem/",
  youtube: "https://www.youtube.com/@AfroFanthem",
  spotify: "https://open.spotify.com/artist/2p4fVYvsZF323pRXdhUEDC?si=xTM6GHEtTcqNUrPqa7scPA",
  tiktok: "https://www.tiktok.com/@afrofanthem",
  x: "https://x.com/Afrofanthem",
  linktree: "https://linktr.ee/afrofanthem",
  appleMusic: "https://music.apple.com/us/artist/afro-franthem/1875754660",
  buyMeACoffee: "https://buymeacoffee.com/afrofanthem"
};

const DEMANDS = [
  {
    icon: MapPin,
    title: "Local Access to Club Experiences",
    description: "African fans value opportunities to experience clubs in-market through tours, activations, and pre-season engagements."
  },
  {
    icon: Globe,
    title: "Recognition as Global Supporters",
    description: "Fans want to feel recognised as part of the club's global community, not just distant audiences."
  },
  {
    icon: ShoppingBag,
    title: "Access to Official Products",
    description: "There is strong demand for accessible, authentic club merchandise within local markets."
  },
  {
    icon: Users,
    title: "Participation, Not Just Viewership",
    description: "One-way communication is no longer enough. Fans want a two-way relationship where they can engage, contribute, and feel involved."
  },
  {
    icon: Heart,
    title: "Value Beyond Viewership",
    description: "Supporters want their loyalty to translate into meaningful connection, access, and benefits."
  },
  {
    icon: Ticket,
    title: "Accessible Membership Models",
    description: "Clear interest in structured membership options tailored to international fans, with accessible pricing and relevant benefits."
  },
  {
    icon: CreditCard,
    title: "Localised Payment Options",
    description: "Fans require payment methods that reflect how transactions happen in African markets, including mobile money and hybrid systems."
  },
  {
    icon: MessageCircle,
    title: "Direct and Meaningful Communication",
    description: "Supporters value more direct, relevant, and personalised communication beyond generic global messaging."
  },
  {
    icon: Network,
    title: "Community and Fan Networks",
    description: "Strong appetite for organised fan communities that connect supporters locally and across regions."
  },
  {
    icon: Shield,
    title: "Trusted and Consistent Access",
    description: "Fans want reliable, official channels to engage with clubs, reducing reliance on informal or counterfeit markets."
  }
];

// Countdown hook
function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  function calculateTimeLeft() {
    const difference = new Date(targetDate) - new Date();
    if (difference <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    
    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
      minutes: Math.floor((difference / 1000 / 60) % 60),
      seconds: Math.floor((difference / 1000) % 60)
    };
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return timeLeft;
}

// Prize draw deadline: August 31, 2026 at midnight UTC
const PRIZE_DRAW_DEADLINE = "2026-08-31T23:59:59Z";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [forumTopics, setForumTopics] = useState([]);
  const countdown = useCountdown(PRIZE_DRAW_DEADLINE);
  const { t } = useTranslation();

  useEffect(() => {
    fetch(`${API_URL}/api/forum/trending?limit=6`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.topics) setForumTopics(data.topics); })
      .catch(() => {});
  }, []);

  const DEMAND_ICONS = [MapPin, Globe, ShoppingBag, Users, Heart, Ticket, CreditCard, MessageCircle, Network, Shield];
  const demandItems = t("demands.items") || DEMANDS.map(d => ({ title: d.title, desc: d.description }));

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={LOGO_URL} 
                alt="Afro Fanthem" 
                className="h-10 md:h-14 w-auto"
                data-testid="nav-logo"
              />
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-6">
              <LanguageSelector />
              <a href="#demands" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-demands">
                {t("nav.vision")}
              </a>
              <a href="#why" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-why">
                {t("nav.why")}
              </a>
              <Link to="/about" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-about">
                {t("nav.about")}
              </Link>
              <Link to="/forum" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-forum">
                Forum
              </Link>
              <a href="#support" className="text-gray-300 hover:text-white transition-colors font-medium" data-testid="nav-support">
                {t("nav.support")}
              </a>
              <Link 
                to="/survey" 
                className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#F59E0B] transition-all hover:-translate-y-0.5"
                data-testid="nav-join-button"
              >
                {t("nav.survey")}
              </Link>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-white/10">
              <div className="flex flex-col gap-4">
                <LanguageSelector className="mb-2" />
                <a href="#demands" className="text-gray-300 hover:text-white transition-colors font-medium py-2">
                  {t("nav.vision")}
                </a>
                <a href="#why" className="text-gray-300 hover:text-white transition-colors font-medium py-2">
                  {t("nav.why")}
                </a>
                <Link to="/about" className="text-gray-300 hover:text-white transition-colors font-medium py-2">
                  {t("nav.about")}
                </Link>
                <Link to="/forum" className="text-gray-300 hover:text-white transition-colors font-medium py-2">
                  Forum
                </Link>
                <a href="#support" className="text-gray-300 hover:text-white transition-colors font-medium py-2">
                  {t("nav.support")}
                </a>
                <Link 
                  to="/survey" 
                  className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 text-center hover:bg-[#F59E0B] transition-all"
                >
                  {t("nav.survey")}
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-4xl">
            {/* Overline */}
            <p className="text-[#FBBF24] text-xs uppercase tracking-[0.3em] font-bold mb-6 animate-fade-in" data-testid="hero-overline">
              {t("hero.overline")}
            </p>

            {/* Main Headline */}
            <h1 
              className="font-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl text-white uppercase tracking-tight leading-none mb-6 animate-slide-up"
              data-testid="hero-headline"
            >
              {t("hero.headline1")}<br />
              <span className="text-[#FBBF24]">{t("hero.headline2")}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-300 max-w-2xl mb-10 leading-relaxed animate-fade-in stagger-2" data-testid="hero-subheadline">
              {t("hero.subtitle")}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in stagger-3">
              <Link 
                to="/survey"
                className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all hover:-translate-y-1 active:translate-y-0"
                data-testid="hero-join-button"
              >
                {t("hero.cta")}
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link 
                to="/forum"
                className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/20 text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-white/10 transition-colors"
                data-testid="hero-debate-button"
              >
                {t("hero.ctaDebate") || "Join the Debate"}
                <ChevronRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mt-16 pt-8 border-t border-white/10 animate-fade-in stagger-4">
              <div>
                <p className="font-heading text-3xl sm:text-4xl text-[#FBBF24]">54+</p>
                <p className="text-sm text-gray-400 uppercase tracking-wide">{t("hero.nations")}</p>
              </div>
              <div>
                <p className="font-heading text-3xl sm:text-4xl text-[#FBBF24]">1B+</p>
                <p className="text-sm text-gray-400 uppercase tracking-wide">{t("hero.fans")}</p>
              </div>
              <div>
                <p className="font-heading text-3xl sm:text-4xl text-[#FBBF24]">1</p>
                <p className="text-sm text-gray-400 uppercase tracking-wide">{t("hero.voice")}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/30 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Why This Matters Section */}
      <section id="why" className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-center">
            {/* Image */}
            <div className="md:col-span-5 relative">
              <div className="aspect-[4/5] rounded-sm overflow-hidden">
                <img 
                  src={WHY_IMAGE}
                  alt="English football stadium with fans"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#FBBF24]/20 rounded-sm" />
            </div>

            {/* Content */}
            <div className="md:col-span-7">
              <p className="text-[#10B981] text-xs uppercase tracking-[0.3em] font-bold mb-4">
                {t("why.overline")}
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-6" data-testid="why-headline">
                {t("why.headline1")}<br />
                <span className="text-[#FBBF24]">{t("why.headline2")}</span>
              </h2>
              <div className="space-y-4 text-gray-300 leading-relaxed">
                <p>{t("why.p1")}</p>
                <p>{t("why.p2")}</p>
                <p>{t("why.p3")}</p>
              </div>

              <Link 
                to="/survey"
                className="inline-flex items-center gap-2 mt-8 text-[#FBBF24] font-bold uppercase tracking-widest hover:gap-4 transition-all"
                data-testid="why-cta-link"
              >
                {t("why.cta")}
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Survey CTA Section */}
      <section id="survey-cta" className="py-20 md:py-28 bg-[#141414] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight mb-6" data-testid="survey-cta-headline">
              Take the African Football <span className="text-[#FBBF24]">Fan Survey</span>
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-4 max-w-3xl mx-auto">
              Help prove the scale, passion, and influence of African football fans. Your response helps Afro Fanthem build evidence that clubs, leagues, media, and brands cannot ignore.
            </p>
            <p className="text-gray-400 leading-relaxed mb-10 max-w-3xl mx-auto">
              We are collecting fan responses to build a public-facing African Football Fan Report. The aim is to show the scale, culture, spending power, and emotional investment of African supporters.
            </p>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10 text-left max-w-3xl mx-auto">
              {[
                "Takes only a few minutes.",
                "Open to African football fans worldwide.",
                "Individual responses will not be publicly published.",
                "Findings will help build the African Football Fan Report.",
                "Fans can share the survey after completing it."
              ].map((point, i) => (
                <div key={point} className={`flex items-start gap-3 ${i >= 3 ? "sm:col-span-1" : ""}`}>
                  <CheckCircle2 className="w-5 h-5 text-[#10B981] flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">{point}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/survey"
                className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all hover:-translate-y-1"
                data-testid="survey-cta-button"
              >
                Take the Survey
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/data-policy"
                className="inline-flex items-center justify-center gap-2 border border-white/20 text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-white/10 transition-colors"
                data-testid="survey-cta-data-link"
              >
                <FileText className="w-5 h-5" />
                Learn How Data Is Used
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trending Debates - Forum as Gateway */}
      <section id="forum-preview" className="py-20 md:py-28 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-[#FBBF24] text-xs uppercase tracking-[0.3em] font-bold mb-4">Trending Now</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight mb-4" data-testid="forum-preview-headline">
              What Fans Are <span className="text-[#FBBF24]">Debating</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              African football fans are sharing their stories, challenging each other, and making their voices heard. Take the survey to join the conversation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {(forumTopics.length > 0 ? forumTopics : [
              { topic_id: null, title: "Why do you support your Premier League club?", category_id: "diaspora-supporters", comment_count: 0 },
              { topic_id: null, title: "Which club has the strongest African fanbase?", category_id: "fan-stories", comment_count: 0 },
              { topic_id: null, title: "Which African player made you fall in love with your club?", category_id: "fan-stories", comment_count: 0 },
              { topic_id: null, title: "What should Premier League clubs do more for African fans?", category_id: "diaspora-supporters", comment_count: 0 },
              { topic_id: null, title: "Which Afro Fanthem anthem should come next?", category_id: "music-chants-anthems", comment_count: 0 },
              { topic_id: null, title: "AFCON memories that define African football", category_id: "afcon-african-football", comment_count: 0 }
            ]).map((topic, i) => {
              const categoryLabels = { "diaspora-supporters": "Diaspora Supporters", "music-chants-anthems": "Music, Chants and Anthems", "fan-stories": "Fan Stories", "afcon-african-football": "AFCON / African Football" };
              return (
                <Link
                  key={topic.topic_id || i}
                  to={topic.topic_id ? `/forum/topic/${topic.topic_id}` : "/forum"}
                  className="bg-white/5 border border-white/10 p-6 hover:border-[#FBBF24]/30 transition-all group"
                  data-testid={`forum-topic-${i}`}
                >
                  <span className="text-[#FBBF24] text-xs uppercase tracking-wider font-bold">{categoryLabels[topic.category_id] || topic.category_id}</span>
                  <h3 className="text-white font-bold mt-2 mb-3 group-hover:text-[#FBBF24] transition-colors leading-snug">{topic.title}</h3>
                  <div className="flex items-center gap-2 text-gray-500 text-sm">
                    <MessageCircle className="w-4 h-4" />
                    <span>{topic.comment_count} {topic.comment_count === 1 ? "reply" : "replies"}</span>
                    <span className="ml-auto text-[#FBBF24] text-xs uppercase tracking-wider font-bold opacity-0 group-hover:opacity-100 transition-opacity">Read &amp; reply</span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="bg-[#FBBF24]/5 border border-[#FBBF24]/20 p-6 sm:p-8 max-w-3xl mx-auto text-center">
            <h3 className="font-heading text-2xl text-white uppercase mb-3">Want to Join the Debate?</h3>
            <p className="text-gray-400 mb-6">Complete the survey to unlock your Fan Pass and start commenting. It takes a few minutes — no profile or password needed.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/survey"
                className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all hover:-translate-y-1"
                data-testid="trending-survey-cta"
              >
                Take the Survey
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/forum"
                className="inline-flex items-center gap-2 border border-white/20 text-white font-bold uppercase tracking-widest px-8 py-4 hover:bg-white/10 transition-colors"
                data-testid="forum-preview-cta"
              >
                Browse Topics
              </Link>
            </div>
            <p className="text-gray-600 text-xs mt-4">Already took the survey? <Link to="/forum/join" className="text-[#FBBF24] hover:underline">Recover your access</Link></p>
          </div>
        </div>
      </section>

      {/* Our Demands Section */}
      <section id="demands" className="py-20 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-[#FBBF24] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              {t("demands.overline")}
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-4" data-testid="demands-headline">
              {t("demands.headline")}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t("demands.subtitle")}
            </p>
          </div>

          {/* Demands Grid - 2 columns for better readability with 10 items */}
          <div className="grid md:grid-cols-2 gap-6">
            {demandItems.map((item, index) => (
              <div 
                key={item.title}
                className="group bg-white/5 border border-white/10 p-8 hover:border-[#FBBF24]/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(251,191,36,0.1)] card-hover"
                data-testid={`demand-card-${index}`}
              >
                <div className="w-14 h-14 bg-[#FBBF24]/10 flex items-center justify-center mb-6 group-hover:bg-[#FBBF24]/20 transition-colors">
                  {(() => { const Icon = DEMAND_ICONS[index] || Globe; return <Icon className="w-7 h-7 text-[#FBBF24]" strokeWidth={1.5} />; })()}
                </div>
                <h3 className="font-heading text-2xl text-white uppercase mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {item.desc || item.description}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center mt-16">
            <Link 
              to="/survey"
              className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-8 py-4 hover:bg-[#F59E0B] transition-all hover:-translate-y-1"
              data-testid="demands-cta-button"
            >
              {t("demands.cta")}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Keynote */}
          <div className="mt-16 md:mt-20 relative">
            <div className="absolute -top-4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FBBF24] to-transparent" />
            <div className="absolute -bottom-4 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FBBF24] to-transparent" />
            
            <div className="bg-[#0A0A0A] border-l-4 border-[#FBBF24] p-5 sm:p-8 md:p-12">
              <h3 className="font-heading text-xl sm:text-2xl md:text-3xl text-[#FBBF24] uppercase mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                <Heart className="w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0" />
                <span>{t("keynote.title")}</span>
              </h3>
              
              <div className="space-y-4 sm:space-y-6 text-gray-300 text-base sm:text-lg leading-relaxed max-w-4xl">
                <p>
                  <span className="text-white font-semibold">{t("keynote.p1title")}</span>{t("keynote.p1")}
                </p>
                <p>
                  <span className="text-white font-semibold">{t("keynote.p2title")}</span>{t("keynote.p2")}
                </p>
                <p>
                  <span className="text-white font-semibold">{t("keynote.p3title")}</span>{t("keynote.p3")}
                </p>
                <p className="text-[#FBBF24] font-semibold text-base sm:text-lg md:text-xl border-t border-white/10 pt-4 sm:pt-6 mt-4 sm:mt-6">
                  {t("keynote.conclusion")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prize Draw Banner with Countdown */}
      <section className="py-8 bg-[#141414] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-center lg:text-left">
              <Trophy className="w-10 h-10 text-[#FBBF24] hidden sm:block" />
              <div>
                <h3 className="font-heading text-xl md:text-2xl text-white uppercase">Win Exclusive Club Merchandise</h3>
                <p className="text-gray-400 text-sm">100 winners selected at random. Complete the survey with a phone number to enter.</p>
              </div>
            </div>
            
            {/* Subtle Countdown */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3 text-sm">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-gray-400">{t("prize.endsIn")}</span>
                <div className="flex items-center gap-2 font-mono">
                  <span className="text-white">{countdown.days}<span className="text-gray-500 text-xs ml-0.5">d</span></span>
                  <span className="text-gray-600">:</span>
                  <span className="text-white">{countdown.hours.toString().padStart(2, '0')}<span className="text-gray-500 text-xs ml-0.5">h</span></span>
                  <span className="text-gray-600">:</span>
                  <span className="text-white">{countdown.minutes.toString().padStart(2, '0')}<span className="text-gray-500 text-xs ml-0.5">m</span></span>
                </div>
              </div>
              
              <Link 
                to="/survey"
                className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-6 py-3 text-sm hover:bg-[#F59E0B] transition-colors whitespace-nowrap"
                data-testid="prize-cta-button"
              >
                Take the Survey
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section id="support" className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-[#10B981] text-xs uppercase tracking-[0.3em] font-bold mb-4">
              {t("supportSection.overline")}
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl text-white uppercase tracking-tight mb-4" data-testid="support-headline">
              {t("supportSection.headline")}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {t("supportSection.subtitle")}
            </p>
          </div>

          {/* Social Links Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Instagram */}
            <a 
              href={SOCIAL_LINKS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-pink-500/50 hover:bg-pink-500/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-instagram"
            >
              <Instagram className="w-8 h-8 text-pink-500" />
              <span className="text-white font-bold uppercase tracking-wider text-sm">Instagram</span>
            </a>

            {/* YouTube */}
            <a 
              href={SOCIAL_LINKS.youtube}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-red-500/50 hover:bg-red-500/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-youtube"
            >
              <Youtube className="w-8 h-8 text-red-500" />
              <span className="text-white font-bold uppercase tracking-wider text-sm">YouTube</span>
            </a>

            {/* Spotify */}
            <a 
              href={SOCIAL_LINKS.spotify}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-green-500/50 hover:bg-green-500/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-spotify"
            >
              <Music className="w-8 h-8 text-green-500" />
              <span className="text-white font-bold uppercase tracking-wider text-sm">Spotify</span>
            </a>

            {/* TikTok */}
            <a 
              href={SOCIAL_LINKS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-white/50 hover:bg-white/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-tiktok"
            >
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
              <span className="text-white font-bold uppercase tracking-wider text-sm">TikTok</span>
            </a>

            {/* X (Twitter) */}
            <a 
              href={SOCIAL_LINKS.x}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-white/50 hover:bg-white/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-x"
            >
              <svg className="w-8 h-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span className="text-white font-bold uppercase tracking-wider text-sm">X</span>
            </a>

            {/* Apple Music */}
            <a 
              href={SOCIAL_LINKS.appleMusic}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-pink-400/50 hover:bg-pink-400/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-apple-music"
            >
              <Music className="w-8 h-8 text-pink-400" />
              <span className="text-white font-bold uppercase tracking-wider text-sm">Apple Music</span>
            </a>

            {/* Linktree */}
            <a 
              href={SOCIAL_LINKS.linktree}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-[#43E55E]/50 hover:bg-[#43E55E]/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-linktree"
            >
              <ExternalLink className="w-8 h-8 text-[#43E55E]" />
              <span className="text-white font-bold uppercase tracking-wider text-sm">Linktree</span>
            </a>

            {/* Buy Me a Coffee */}
            <a 
              href={SOCIAL_LINKS.buyMeACoffee}
              target="_blank"
              rel="noopener noreferrer"
              className="group bg-white/5 border border-white/10 p-6 hover:border-[#FBBF24]/50 hover:bg-[#FBBF24]/5 transition-all flex flex-col items-center gap-3"
              data-testid="social-buymeacoffee"
            >
              <Coffee className="w-8 h-8 text-[#FBBF24]" />
              <span className="text-white font-bold uppercase tracking-wider text-sm">Donate</span>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-32 bg-[#141414] relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img 
            src={CTA_BG}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6" data-testid="final-cta-headline">
            {t("finalCta.headline1")}<br />
            <span className="text-[#FBBF24]">{t("finalCta.headline2")}</span>
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            {t("finalCta.subtitle")}
          </p>
          <Link 
            to="/survey"
            className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-10 py-5 text-lg hover:bg-[#F59E0B] transition-all hover:-translate-y-1"
            data-testid="final-cta-button"
          >
            {t("finalCta.cta")}
            <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
